import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { CONSTANTS } from '@pk/powerkioskutils';
import { RouterService } from 'src/app/core/router.service';

import { AppStateService } from '../../core/app-state.service';
import { SecurityService } from '../../security/security.service';
import { User } from '../../shared/models';

@Injectable({
	providedIn: 'root',
})
export class AuthResolver {
	constructor(
		private securityService: SecurityService,
		private appStateService: AppStateService,
		private routerService: RouterService,
	) {}

	async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
		try {
			const res = await this.securityService.getAuthFields();
			if (res.token && res.refreshToken && res.loggedInUser) {
				this.securityService.setAuthFields(res.token, res.refreshToken, res.loggedInUser);
				const user = new User(res.loggedInUser);
				this.securityService.isDirect = false;
				this.appStateService.setReady({ app: true, direct: false, billReader: false, isPopupWindow: route.data.isPopupWindow });
				const isImpersonating = this.securityService.isImpersonating;

				const redirectRoles = [CONSTANTS.userRoles.customer.roleId, CONSTANTS.userRoles.lead.roleId];
				if (user?.roleId && redirectRoles.includes(user?.roleId)) {
					await this.returnToLogin(state);
					return false;
				}

				if (await this.checkShouldRedirectPasswordExpired(user, state, isImpersonating)) {
					return false;
				}
				if (await this.checkShouldRedirectFailedPayment(user, state, isImpersonating)) {
					return false;
				}
				if (await this.checkShouldRedirectInactiveBroker(user, route, isImpersonating)) {
					return false;
				}

				if (user.isAdmin || user.isOwner) {
					if (await this.checkShouldRedirectAdmin(route)) {
						return false;
					}
				} else if (user.supplierId) {
					if (await this.checkShouldRedirectSupplier(user, route)) {
						return false;
					}
				} else if (user.agent?.isEnterprise) {
					if (await this.checkShouldRedirectEnterprise(user, route)) {
						return false;
					}
				} else {
					if (await this.checkShouldRedirectOthers(user, route)) {
						return false;
					}
				}

				if (route.data.showFeedback) {
					this.appStateService.showFeedback(route.data.showFeedback);
				} else {
					this.appStateService.showFeedback('Other');
				}

				return true;
			}
		} catch (e) {
			// nothing
		}

		await this.returnToLogin(state);
		return false;
	}

	private async returnToLogin(state: RouterStateSnapshot): Promise<void> {
		this.securityService.isDirect = false;
		this.appStateService.setReady({ app: false, direct: false, billReader: false, isPopupWindow: false });
		await this.routerService.navigate(['/security'], { queryParams: { returnUrl: state.url } });
	}

	private async checkShouldRedirectAdmin(route: ActivatedRouteSnapshot): Promise<boolean> {
		if (route.data.isAdmin || route.data.isOwner) {
			return false;
		}

		// admins must impersonate suppliers to view the supplier portal
		if (route.data.isSupplier) {
			await this.routerService.navigate(['/contract']);
			return true;
		}

		// admins must imperonate brokers to view certain pages that are broker specific
		if (route.data.isBroker) {
			await this.routerService.navigate(['/contract']);
			return true;
		}

		return false;
	}

	private async checkShouldRedirectSupplier(user: User, route: ActivatedRouteSnapshot): Promise<boolean> {
		if (route.data.isSupplier) {
			return false;
		}

		// Suppliers must be explicitly given access to routes
		if (!user || user.roleId === CONSTANTS.userRoles.supplierCommercial.roleId) {
			await this.routerService.navigate(['/supplier']);
		} else if (user && user.roleId === CONSTANTS.userRoles.supplierResidential.roleId) {
			await this.routerService.navigate(['/supplier/residential-confirmations']);
		}
		return true;
	}

	private async checkShouldRedirectEnterprise(user: User, route: ActivatedRouteSnapshot): Promise<boolean> {
		if (route.data.isEnterprise) {
			return false;
		}
		const canShowEnergyPortal = user.agent?.showEnergyPortal && (!user.agent?.parent || user.agent?.parent?.showEnergyPortal);
		if (route.data.isEnergyPortal && !canShowEnergyPortal) {
			await this.routerService.navigate(['/contract']);
			return true;
		}
		if (!route.data.isEnergyPortal && canShowEnergyPortal) {
			await this.routerService.navigate(['/enterprise']);
			return true;
		}

		return false;
	}

	private async checkShouldRedirectOthers(user: User, route: ActivatedRouteSnapshot): Promise<boolean> {
		if (route.data.isBroker) {
			return false;
		}

		if (route.data.isEnergyPortal && (!user.agent?.showEnergyPortal || (user.agent?.parent && !user.agent?.parent?.showEnergyPortal))) {
			await this.routerService.navigate(['/contract']);
			return true;
		}

		// only admins allowed here
		if (route.data.isAdmin || route.data.isOwner) {
			await this.routerService.navigate(['/contract']);
			return true;
		}

		return false;
	}

	private async checkShouldRedirectPasswordExpired(user: User, state: RouterStateSnapshot, isImpersonating: boolean): Promise<boolean> {
		if (!isImpersonating && user.isPasswordExpired && !state.url.includes('security/edit')) {
			await this.routerService.navigate(['/security/edit']);
			return true;
		}
		return false;
	}

	private async checkShouldRedirectFailedPayment(user: User, state: RouterStateSnapshot, isImpersonating: boolean): Promise<boolean> {
		if (!isImpersonating && user.agent && user.agent.hasFailedPayment && !state.url.includes('security/edit#manageCreditCard')) {
			if (user.agent.parent) {
				await this.routerService.navigate(['/security/edit']);
			} else {
				await this.routerService.navigate(['/security/edit'], { fragment: 'manageCreditCard' });
			}
			return true;
		}
		return false;
	}

	private async checkShouldRedirectInactiveBroker(user: User, route: ActivatedRouteSnapshot, isImpersonating: boolean): Promise<boolean> {
		// can remove hasOwnProperty check after a few months
		if (!isImpersonating && user.agent && user.agent.hasOwnProperty('isActive') && !user.agent.isActive && !route.data.allowInactive) {
			await this.routerService.navigate(['/security/edit']);
			return true;
		}
		return false;
	}
}
